import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, UsersIcon, ArrowPathIcon, ClockIcon, PuzzlePieceIcon, UserGroupIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { DefaultService, Player, PlayerWithLeagueContext, GameOutput, PlayOutput } from '../generated-api';
import { getRatingHistories } from '../services/ratingHistories';
import LeagueTabNavigation from '../components/LeagueTabNavigation';
import LeagueTeamResults from '../components/LeagueTeamResults';
import ActionButtons from '../components/common/ActionButtons';
import ImageModal from '../components/common/ImageModal';
import PhotoCarousel from '../components/common/PhotoCarousel';
import { ReactionButton } from '../components/reactions';
import { useData } from '../contexts/DataContext';
import { getAllMetadataFields } from '../constants/metadataFields';
import { trackAction } from '../services/telemetry';

const PlayDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { games, leagues, fetchGames, fetchLeagues } = useData();

  const [play, setPlay] = useState<PlayOutput | null>(null);
  const [players, setPlayers] = useState<(Player | PlayerWithLeagueContext)[]>([]);
  const [selectedGame, setSelectedGame] = useState<GameOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Multi-league data structures
  const [leagueRatingChanges, setLeagueRatingChanges] = useState<Record<string, Record<string, number>>>({});
  const [leagueRatingsBefore, setLeagueRatingsBefore] = useState<Record<string, Record<string, { rating: number; sigma: number }>>>({});
  const [leagueRatingsAfter, setLeagueRatingsAfter] = useState<Record<string, Record<string, { rating: number; sigma: number }>>>({});
  const [leagueWinProbabilities, setLeagueWinProbabilities] = useState<Record<string, Record<string, number>>>({});
  const [selectedLeagueTab, setSelectedLeagueTab] = useState<string>('');
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadPlayData = async () => {
      if (!id) return;

      try {
        setError(null);
        // Use cached games and leagues
        const [playData, playersData, gamesData] = await Promise.all([
          DefaultService.getPlayPlaysPlayIdGet({ playId: id }),
          DefaultService.getPlayersPlayersGet(),
          fetchGames(),
          fetchLeagues()
        ]);

        setPlay(playData);
        setPlayers(playersData);

        // Find the selected game
        const game = gamesData.find(g => g.id === playData.gameId);
        setSelectedGame(game || null);

        // Calculate league-aware rating changes for each player
        await calculateLeagueRatingChanges(playData, playersData);

        // Calculate league-specific win probabilities using backend API
        await calculateLeagueWinProbabilities(playData.id, playData);

        // Set default selected league tab to first league in the play
        if (playData.leagueIds && playData.leagueIds.length > 0) {
          setSelectedLeagueTab(playData.leagueIds[0]);
        }
        
      } catch (err) {
        setError('Failed to load play data. Please try again.');
        console.error('Error loading play:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPlayData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // fetchGames and fetchLeagues are stable from useData context

  const calculateLeagueRatingChanges = async (playData: PlayOutput, playersData: Player[]) => {
    const leagueChanges: Record<string, Record<string, number>> = {};
    const leagueRatingsBefore: Record<string, Record<string, { rating: number; sigma: number }>> = {};
    const leagueRatingsAfter: Record<string, Record<string, { rating: number; sigma: number }>> = {};

    // Initialize league data structures
    for (const leagueId of playData.leagueIds) {
      leagueChanges[leagueId] = {};
      leagueRatingsBefore[leagueId] = {};
      leagueRatingsAfter[leagueId] = {};
    }

    // Get all player IDs involved in this play
    const allPlayerIds = Object.values(playData.teams).flat();

    // One request per league, always fresh so a just-logged play is found
    const leagueHistories = await Promise.all(
      playData.leagueIds.map(leagueId =>
        getRatingHistories(leagueId, { playerIds: allPlayerIds, fresh: true }).catch(error => {
          console.warn(`Failed to get rating histories in league ${leagueId}:`, error);
          return null;
        })
      )
    );

    // Process results
    playData.leagueIds.flatMap((leagueId, index) =>
      allPlayerIds.map(playerId => ({ leagueId, playerId, ratingHistory: leagueHistories[index]?.[playerId] }))
    ).forEach(({ leagueId, playerId, ratingHistory }) => {
      if (!ratingHistory) return;

      // Find the rating change for this specific play
      const progressionIndex = ratingHistory.ratingProgression?.findIndex(
        (entry: any) => entry.playId === playData.id
      );

      if (progressionIndex !== undefined && progressionIndex >= 0) {
        const progressionEntry = ratingHistory.ratingProgression[progressionIndex];
        leagueChanges[leagueId][playerId] = Math.round(progressionEntry.ratingChange * 100) / 100;
        
        const ratingAfterValue = progressionEntry.ratingAfter.rating;
        const sigmaAfter = progressionEntry.ratingAfter.sigma;
        const ratingBeforeValue = ratingAfterValue - progressionEntry.ratingChange;
        
        // Get sigma before from previous entry, or use default (8.333) if first entry
        let sigmaBefore: number;
        if (progressionIndex > 0) {
          const previousEntry = ratingHistory.ratingProgression[progressionIndex - 1];
          sigmaBefore = previousEntry.ratingAfter.sigma;
        } else {
          // First entry - use default TrueSkill sigma
          sigmaBefore = 8.333;
        }
        
        leagueRatingsBefore[leagueId][playerId] = {
          rating: Math.round(ratingBeforeValue * 100) / 100,
          sigma: Math.round(sigmaBefore * 100) / 100
        };
        leagueRatingsAfter[leagueId][playerId] = {
          rating: Math.round(ratingAfterValue * 100) / 100,
          sigma: Math.round(sigmaAfter * 100) / 100
        };
      }
    });

    setLeagueRatingChanges(leagueChanges);
    setLeagueRatingsBefore(leagueRatingsBefore);
    setLeagueRatingsAfter(leagueRatingsAfter);
  };

  const calculateLeagueWinProbabilities = async (playId: string, playData: PlayOutput) => {
    const leagueProbabilities: Record<string, Record<string, number>> = {};

    // Build all win probability requests upfront for parallel execution
    const winProbabilityRequests = playData.leagueIds.map(leagueId => ({
      leagueId,
      promise: DefaultService.getPlayWinProbabilitiesPlaysPlayIdWinProbabilitiesGet({
        playId: playId,
        leagueId: leagueId
      }).catch(error => {
        console.warn(`Failed to calculate win probabilities for league ${leagueId}:`, error);
        return {};
      })
    }));

    // Execute all requests in parallel
    const results = await Promise.all(winProbabilityRequests.map(r => r.promise));

    // Process results
    results.forEach((probabilities, index) => {
      leagueProbabilities[winProbabilityRequests[index].leagueId] = probabilities;
    });

    setLeagueWinProbabilities(leagueProbabilities);
  };

  const getLeagueRelevantRanking = (leagueId: string): { teamName: string; playerIds: string[]; position: number }[] => {
    if (!play) return [];

    // Get league members
    const league = leagues.find(l => l.id === leagueId);
    if (!league) return [];

    const leaguePlayerIds = new Set(league.playerIds);

    // Filter original ranking to only include league members
    const filteredRanking: (string | string[])[] = [];

    for (const rankItem of play.ranking) {
      if (typeof rankItem === 'string') {
        // Single team - check if any players in this team are league members
        const teamPlayerIds = play.teams[rankItem] || [];
        const leagueMembersInTeam = teamPlayerIds.filter(playerId => leaguePlayerIds.has(playerId));

        if (leagueMembersInTeam.length > 0) {
          filteredRanking.push(rankItem);
        }
      } else {
        // Tie (multiple teams) - filter teams that have league members
        const teamsWithLeagueMembers = rankItem.filter(teamName => {
          const teamPlayerIds = play.teams[teamName] || [];
          return teamPlayerIds.some(playerId => leaguePlayerIds.has(playerId));
        });

        if (teamsWithLeagueMembers.length > 0) {
          if (teamsWithLeagueMembers.length === 1) {
            filteredRanking.push(teamsWithLeagueMembers[0]);
          } else {
            filteredRanking.push(teamsWithLeagueMembers);
          }
        }
      }
    }

    // Calculate positions based on filtered ranking
    return calculateTeamPositionsFromRanking(filteredRanking);
  };

  const calculateTeamPositionsFromRanking = (ranking: (string | string[])[]): { teamName: string; playerIds: string[]; position: number }[] => {
    const teamsWithPositions: { teamName: string; playerIds: string[]; position: number }[] = [];
    let currentPosition = 1;

    for (const rankItem of ranking) {
      if (typeof rankItem === 'string') {
        // Single team at this position
        const playerIds = play?.teams[rankItem] || [];
        teamsWithPositions.push({
          teamName: rankItem,
          playerIds,
          position: currentPosition
        });
        currentPosition += playerIds.length;
      } else {
        // Tie - multiple teams at same position
        for (const teamName of rankItem) {
          const playerIds = play?.teams[teamName] || [];
          teamsWithPositions.push({
            teamName,
            playerIds,
            position: currentPosition
          });
        }
        // Calculate position increment for tie
        const totalPlayersInTie = rankItem.reduce((sum, teamName) => {
          return sum + (play?.teams[teamName]?.length || 0);
        }, 0);
        currentPosition += totalPlayersInTie;
      }
    }

    return teamsWithPositions;
  };

  const handleDeletePlay = async () => {
    if (!play) return;
    
    if (!window.confirm('Are you sure you want to delete this play session? This action cannot be undone and will not recalculate ratings automatically.')) {
      return;
    }

    try {
      await DefaultService.deletePlayPlaysPlayIdDelete({ playId: play.id });
      trackAction('play_deleted', { from: 'detail' });
      navigate('/plays');
    } catch (err) {
      setError('Failed to delete play session. Please try again.');
      console.error('Error deleting play:', err);
    }
  };

  const getPlayerName = (playerId: string): string => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : `Player ${playerId.slice(-4)}`;
  };

  const handleReactionChange = async () => {
    // Refresh play data to get updated reaction summary
    if (!id) return;
    try {
      const playData = await DefaultService.getPlayPlaysPlayIdGet({ playId: id });
      setPlay(playData);
    } catch (err) {
      console.error('Error refreshing play data after reaction change:', err);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64" data-testid="play-detail-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!play) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-heading mb-4">Play Session Not Found</h2>
        <p className="text-body mb-6">The play session you're looking for doesn't exist.</p>
        <Link
          to="/plays"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Play Sessions
        </Link>
      </div>
    );
  }

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  const displayGameName = selectedGame?.name || games.find(g => g.id === play.gameId)?.name || 'Unknown Game';
  const allPlayerIds = Object.values(play.teams).flat();

  return (
    <div className="container mx-auto px-0 py-4 sm:px-4 sm:py-8">
      {/* Back link */}
      <button
        onClick={() => navigate('/plays')}
        className="inline-flex items-center min-h-[44px] sm:min-h-0 text-primary-700 hover:text-primary-800 mb-4 text-sm font-medium px-4 sm:px-0"
      >
        ← Back to Play Sessions
      </button>

      {/* Error Message */}
      {error && (
        <div className="mx-4 sm:mx-0 mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Card */}
      <div className="bg-surface sm:shadow-sm sm:rounded-xl sm:border sm:border-edge overflow-hidden mb-4 sm:mb-8 relative">
        <div className="p-4 sm:p-6">
          <div className="flex gap-4 sm:gap-6">
            {/* Game Image */}
            {selectedGame?.id ? (
              <Link data-track="play_game_link" to={`/games/${selectedGame.id}`} className="flex-shrink-0">
                {selectedGame.imageUrl ? (
                  <img
                    src={selectedGame.imageUrl}
                    alt={displayGameName}
                    className="w-20 h-20 sm:w-32 sm:h-32 object-contain rounded-xl bg-surface-base hover:opacity-90 transition-opacity"
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-xl bg-surface-subtle flex items-center justify-center hover:bg-surface-skeleton transition-colors">
                    <span className="text-2xl sm:text-4xl text-faint">🎲</span>
                  </div>
                )}
              </Link>
            ) : (
              <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-xl bg-surface-subtle flex items-center justify-center flex-shrink-0">
                <span className="text-2xl sm:text-4xl text-faint">🎲</span>
              </div>
            )}

            {/* Title + meta */}
            <div className="flex-1 min-w-0">
              <h1 className="text-fluid-h1 font-bold text-heading leading-tight sm:pr-28">
                {selectedGame?.id ? (
                  <Link data-track="play_game_link" to={`/games/${selectedGame.id}`} className="inline-flex items-center min-h-[44px] sm:min-h-0 hover:text-primary-700 transition-colors">
                    {displayGameName}
                  </Link>
                ) : (
                  displayGameName
                )}
              </h1>

              {/* Desktop action buttons */}
              <div className="hidden sm:flex items-center gap-2 absolute top-4 right-4 sm:top-6 sm:right-6">
                <Link
                  data-track="play_rematch"
                  to={`/plays/new?rematch=${play.id}`}
                  aria-label="Rematch"
                  className="inline-flex items-center px-4 py-2 border border-edge-input rounded-md shadow-sm text-sm font-medium text-body bg-surface hover:bg-surface-hover"
                >
                  <ArrowPathIcon className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Rematch</span>
                </Link>
                <ActionButtons
                  editLink={`/plays/${play.id}/edit`}
                  onDelete={handleDeletePlay}
                  className="flex-row"
                />
              </div>

              {/* Metadata pills */}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {new Date(play.playDate).toLocaleDateString()}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                  <UserGroupIcon className="h-3.5 w-3.5" />
                  {allPlayerIds.length} players
                </span>
                {play.durationMinutes && play.durationMinutes > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {formatDuration(play.durationMinutes)}
                  </span>
                )}
              </div>

              {/* Extensions - desktop */}
              {play.gameExtensions && play.gameExtensions.length > 0 && (
                <div className="hidden sm:flex flex-wrap gap-1.5 mt-3">
                  {play.gameExtensions.map((ext) => (
                    <span
                      key={ext}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100"
                    >
                      <PuzzlePieceIcon className="h-3 w-3 mr-1" />
                      {ext}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Extensions - mobile only */}
          {play.gameExtensions && play.gameExtensions.length > 0 && (
            <div className="sm:hidden flex flex-wrap gap-1.5 mt-3">
              {play.gameExtensions.map((ext) => (
                <span
                  key={ext}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100"
                >
                  <PuzzlePieceIcon className="h-3 w-3 mr-1" />
                  {ext}
                </span>
              ))}
            </div>
          )}

          {/* Action buttons - mobile only */}
          <div className="sm:hidden flex gap-2 mt-3">
            <Link
              data-track="play_rematch"
              to={`/plays/new?rematch=${play.id}`}
              aria-label="Rematch"
              className="inline-flex items-center justify-center px-4 py-2 border border-edge-input rounded-md shadow-sm text-sm font-medium text-body bg-surface hover:bg-surface-hover"
            >
              <ArrowPathIcon className="h-4 w-4" />
            </Link>
            <ActionButtons
              editLink={`/plays/${play.id}/edit`}
              onDelete={handleDeletePlay}
              className="flex-row"
            />
          </div>
        </div>

        {/* Comment */}
        {play.comment && (
          <div className="border-t border-edge-subtle px-4 sm:px-6 py-4">
            <p className="text-sm text-body italic">"{play.comment}"</p>
          </div>
        )}

        {/* Reactions */}
        <div className="border-t border-edge-subtle px-4 sm:px-6 py-3 flex justify-end">
          <ReactionButton
            playId={play.id}
            reactionSummary={play.reactionSummary}
            userReaction={play.userReaction}
            onReactionChange={handleReactionChange}
          />
        </div>
      </div>

      {/* Play Session Photos */}
      {play.imageUrls && play.imageUrls.length > 0 && (
        <div className="mb-4 sm:mb-8">
          <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-lg sm:p-6">
            <h2 className="text-lg font-medium text-heading mb-4">Play Session Photos</h2>
            <PhotoCarousel
              images={play.imageUrls}
              altTextPrefix="Play session photo"
              onImageClick={setSelectedImageUrl}
            />
          </div>
        </div>
      )}

      {/* League-Aware Teams and Results */}
      <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-lg sm:p-6">
        <h2 className="text-lg font-medium text-heading mb-6 flex items-center">
          <UsersIcon className="h-5 w-5 mr-2" />
          Teams & Results
        </h2>

        {/* League Tab Navigation */}
        <LeagueTabNavigation
          leagues={leagues}
          playLeagueIds={play?.leagueIds || []}
          selectedLeagueId={selectedLeagueTab}
          onLeagueSelect={setSelectedLeagueTab}
        />

        {/* League-Specific Team Results */}
        {selectedLeagueTab && (
          <LeagueTeamResults
            leagueId={selectedLeagueTab}
            teams={getLeagueRelevantRanking(selectedLeagueTab)}
            players={players}
            ratingChanges={leagueRatingChanges[selectedLeagueTab] || {}}
            ratingsBefore={leagueRatingsBefore[selectedLeagueTab] || {}}
            ratingsAfter={leagueRatingsAfter[selectedLeagueTab] || {}}
            winProbabilities={leagueWinProbabilities[selectedLeagueTab] || {}}
            getPlayerName={getPlayerName}
            teamMetadata={play.teamMetadata || undefined}
            metadataFields={getAllMetadataFields(selectedGame?.playerMetadataFields || [])}
          />
        )}
      </div>

      {/* Image Modal */}
      {selectedImageUrl && (
        <ImageModal
          isOpen={!!selectedImageUrl}
          onClose={() => setSelectedImageUrl(null)}
          imageUrl={selectedImageUrl}
          altText="Play session photo"
        />
      )}
    </div>
  );
};

export default PlayDetailPage;